import { normalizeSimulationType } from "@/lib/simulation-audio-upload";

const DB_NAME = "salesably-simulation-audio";
const DB_VERSION = 1;
const STORE_NAME = "pending_uploads";
const MAX_PENDING_AGE_MS = 24 * 60 * 60 * 1000;
const MAX_PENDING_ITEMS = 12;

function canUseIndexedDb() {
  return typeof window !== "undefined" && typeof indexedDB !== "undefined";
}

function requestToPromise(request) {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

function transactionDone(transaction) {
  return new Promise((resolve, reject) => {
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error);
    transaction.onabort = () =>
      reject(transaction.error || new Error("IndexedDB transaction aborted"));
  });
}

function openPendingAudioDb() {
  if (!canUseIndexedDb()) {
    return Promise.resolve(null);
  }

  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = () => {
      const db = request.result;
      if (db.objectStoreNames.contains(STORE_NAME)) {
        return;
      }

      const store = db.createObjectStore(STORE_NAME, { keyPath: "id" });
      store.createIndex("simulationType", "simulationType", { unique: false });
      store.createIndex("claimedResultKey", "claimedResultKey", {
        unique: false,
      });
      store.createIndex("createdAt", "createdAt", { unique: false });
    };

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

async function withStore(mode, handler) {
  const db = await openPendingAudioDb();
  if (!db) {
    return null;
  }

  const transaction = db.transaction(STORE_NAME, mode);
  const store = transaction.objectStore(STORE_NAME);

  try {
    const result = await handler(store);
    await transactionDone(transaction);
    return result;
  } finally {
    db.close();
  }
}

async function getAllEntries() {
  const result = await withStore("readonly", (store) =>
    requestToPromise(store.getAll())
  );
  return Array.isArray(result) ? result : [];
}

function buildResultKey(simulationType, resultId) {
  if (!simulationType || !resultId) return null;
  return `${simulationType}:${String(resultId)}`;
}

function sortNewestFirst(entries = []) {
  return [...entries].sort((a, b) => Number(b.createdAt) - Number(a.createdAt));
}

async function pruneQueue() {
  const now = Date.now();
  const entries = await getAllEntries();
  if (!entries.length) {
    return;
  }

  const expiredIds = entries
    .filter((entry) => now - Number(entry.createdAt || 0) > MAX_PENDING_AGE_MS)
    .map((entry) => entry.id);

  const overflowIds = sortNewestFirst(entries)
    .slice(MAX_PENDING_ITEMS)
    .map((entry) => entry.id);

  const idsToDelete = Array.from(new Set([...expiredIds, ...overflowIds])).filter(
    Boolean
  );

  if (!idsToDelete.length) {
    return;
  }

  await withStore("readwrite", async (store) => {
    await Promise.all(
      idsToDelete.map((id) => requestToPromise(store.delete(id)))
    );
  });
}

export async function enqueuePendingSimulationAudio({
  simulationType,
  blob,
  mimeType = "",
  sourcePath = "",
}) {
  const normalizedType = normalizeSimulationType(simulationType);
  if (!normalizedType || !(blob instanceof Blob) || !canUseIndexedDb()) {
    return null;
  }

  await pruneQueue();

  const id =
    typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  const now = Date.now();
  const entry = {
    id,
    simulationType: normalizedType,
    mimeType: mimeType || blob.type || "audio/webm;codecs=opus",
    blob,
    sourcePath: sourcePath || null,
    claimedResultKey: null,
    attempts: 0,
    lastError: null,
    createdAt: now,
    updatedAt: now,
  };

  await withStore("readwrite", (store) => requestToPromise(store.put(entry)));

  return entry;
}

export async function findPendingSimulationAudioForResult({
  simulationType,
  resultId,
}) {
  const normalizedType = normalizeSimulationType(simulationType);
  if (!normalizedType || !canUseIndexedDb()) {
    return null;
  }

  const resultKey = buildResultKey(normalizedType, resultId);
  const entries = sortNewestFirst(
    (await getAllEntries()).filter(
      (entry) => entry.simulationType === normalizedType
    )
  );

  if (!entries.length) {
    return null;
  }

  if (resultKey) {
    const claimed = entries.find((entry) => entry.claimedResultKey === resultKey);
    if (claimed) {
      return claimed;
    }
  }

  return entries.find((entry) => !entry.claimedResultKey) || null;
}

export async function claimPendingSimulationAudio({
  id,
  simulationType,
  resultId,
}) {
  const normalizedType = normalizeSimulationType(simulationType);
  const resultKey = buildResultKey(normalizedType, resultId);
  if (!id || !resultKey || !canUseIndexedDb()) {
    return null;
  }

  return withStore("readwrite", async (store) => {
    const entry = await requestToPromise(store.get(id));
    if (!entry) {
      return null;
    }
    entry.claimedResultKey = resultKey;
    entry.updatedAt = Date.now();
    await requestToPromise(store.put(entry));
    return entry;
  });
}

export async function markPendingSimulationAudioAttempt({ id, error }) {
  if (!id || !canUseIndexedDb()) {
    return null;
  }

  return withStore("readwrite", async (store) => {
    const entry = await requestToPromise(store.get(id));
    if (!entry) {
      return null;
    }
    entry.attempts = Number(entry.attempts || 0) + 1;
    entry.lastError =
      typeof error === "string"
        ? error.slice(0, 500)
        : error?.message?.slice(0, 500) || "Unknown upload error";
    entry.updatedAt = Date.now();
    await requestToPromise(store.put(entry));
    return entry;
  });
}

export async function removePendingSimulationAudio(id) {
  if (!id || !canUseIndexedDb()) {
    return false;
  }

  await withStore("readwrite", (store) => requestToPromise(store.delete(id)));
  return true;
}
